import Highlight from "@tiptap/extension-highlight"
import TextAlign from "@tiptap/extension-text-align"
import { Editor, EditorContent, useEditor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import classNames from "classnames"
import { Button } from "components/LEGACY/styled-components/Buttons"
import { ImageIcon } from "components/LEGACY/styled-components/Icons"
import Modal from "components/Modal"
import ResizableImageExtension from "components/editor/components/ResizableImage"
import { CollectionReference } from "firebase/firestore"
import { getDownloadURL, uploadBytes } from "firebase/storage"
import { useEffect, useState } from "react"
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  Highlighter,
  Italic,
  Redo,
  Strikethrough,
  Undo,
} from "styled-icons/fa-solid"
import { Photo } from "styled-icons/foundation"
import { ListOrdered, ListUnordered } from "styled-icons/octicons"

import { getImageUploadRefForCollection } from "util-functions/getImageUploadRef"

type TiptapBaseProps = {
  content: string
  relatedCollection?: CollectionReference
}

type TiptapReadonlyProps = {
  readonly: true
} & TiptapBaseProps

type TiptapEditableProps = {
  readonly?: false
  setContent: (content: { json: unknown; html: string; text: string }) => void
} & TiptapBaseProps

type TiptapProps = TiptapReadonlyProps | TiptapEditableProps

export default function Tiptap(props: TiptapProps) {
  const [isUploadImageModalOpen, setIsUploadImageModalOpen] = useState(false)

  const uploadImage = async (file: File) => {
    const targetRef = props.relatedCollection && getImageUploadRefForCollection(props.relatedCollection)

    if (targetRef) {
      const upload = await uploadBytes(targetRef, file)
      const url = await getDownloadURL(upload.ref)
      return url
    }
    console.error("Could not create a targetRef. This is probably caused by a missing relatedDocument prop.")
    return
  }

  const editor = useEditor({
    extensions: [
      StarterKit,
      ResizableImageExtension,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Highlight,
    ],
    editable: !props.readonly,
    content: props.content || "<p>Dein Inhalt...</p>",
    editorProps: {
      attributes: {
        class: classNames(
          !props.readonly && "tw-p-5 tw-rounded-xl tw-border focus:outline-none focus:border-black",
          "tw-prose tw-prose-sm tw-w-full tw-min-w-full focus:tw-outline-none focus:tw-border-green-primary tw-border-black"
        ),
      },
      handleDrop: function (view, event, slice, moved) {
        if (!moved && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]) {
          // if dropping external files
          const file = event.dataTransfer.files[0] // the dropped file
          const filesize = file.size / 1024 / 1024 // get the filesize in MB
          if ((file.type === "image/jpeg" || file.type === "image/png") && filesize < 10) {
            uploadImage(file)
              .then(function (response) {
                // response is the image url for where it has been saved
                // place the now uploaded image in the editor where it was dropped
                const { schema } = view.state
                const coordinates = view.posAtCoords({ left: event.clientX, top: event.clientY })
                console.log("URL", response)
                const node = schema.nodes.image.create({ src: response }) // creates the image element
                if (!coordinates) return false
                const transaction = view.state.tr.insert(coordinates.pos, node) // places it in the correct position
                return view.dispatch(transaction)
              })
              .catch(function (error) {
                if (error) {
                  window.alert("There was a problem uploading your image, please try again.")
                }
              })
          } else {
            window.alert("Images need to be in jpg or png format and less than 10mb in size.")
          }
          return true // handled
        }
        return false // not handled use default behaviour
      },
    },
    onUpdate: !props.readonly
      ? ({ editor }) => {
          props.setContent({ json: editor.getJSON(), html: editor.getHTML(), text: editor.getText() })
        }
      : undefined,
  })

  // Add useEffect to update editor content when props.content changes
  useEffect(() => {
    if (editor && props.content !== editor.getHTML()) {
      editor.commands.setContent(props.content)
    }
  }, [props.content, editor])

  if (!editor) {
    return null
  }

  return (
    <>
      <div className="tw-w-full">
        {!props.readonly && <MenuBar editor={editor} setIsUploadImageModalOpen={setIsUploadImageModalOpen} />}
        <div className="tw-mt-2">
          <EditorContent editor={editor} />
        </div>
      </div>
      {!props.readonly && (
        <UploadImageModal
          isOpen={isUploadImageModalOpen}
          setIsOpen={setIsUploadImageModalOpen}
          handleUpload={image =>
            uploadImage(image)
              .then(function (url) {
                if (!url) return
                editor
                  .chain()
                  .focus()
                  .setImage({ src: url })

                  .run()
              })
              .catch(function (error) {
                if (error) {
                  window.alert("There was a problem uploading your image, please try again.")
                }
              })
          }
        />
      )}
    </>
  )
}

function MenuBar({
  editor,
  setIsUploadImageModalOpen,
}: {
  editor: Editor
  setIsUploadImageModalOpen: React.Dispatch<React.SetStateAction<boolean>>
}) {
  if (!editor) {
    return null
  }

  return (
    <div className="tw-flex tw-flex-row tw-rounded-md tw-bg-gray-200 tw-border tw-border-gray-400 tw-w-fit tw-divide-x tw-divide-gray-400 tw-overflow-hidden">
      <div className="tw-flex">
        <button
          onClick={() => editor.chain().undo().run()}
          className={"tw-px-4 tw-py-2 tw-font-bold hover:tw-bg-gray-300"}
        >
          <Undo className="tw-size-4" />
        </button>
        <button
          onClick={() => editor.chain().redo().run()}
          className={"tw-px-4 tw-py-2 tw-font-bold hover:tw-bg-gray-300"}
        >
          <Redo className="tw-size-4" />
        </button>
      </div>
      <select
        id="type"
        name="type"
        className="tw-block tw-w-full tw-py-2 tw-pl-3 tw-pr-10 tw-mr-2 tw-bg-transparent focus:tw-outline-none"
        defaultValue={
          editor.isActive("heading", { level: 1 })
            ? "h1"
            : editor.isActive("heading", { level: 2 })
              ? "h2"
              : editor.isActive("heading", { level: 3 })
                ? "h3"
                : "paragraph"
        }
        value={
          editor.isActive("heading", { level: 1 })
            ? "h1"
            : editor.isActive("heading", { level: 2 })
              ? "h2"
              : editor.isActive("heading", { level: 3 })
                ? "h3"
                : "paragraph"
        }
        onChange={e => {
          const value = e.target.value
          switch (value) {
            case "h1":
              editor.chain().focus().toggleHeading({ level: 1 }).run()
              break
            case "h2":
              editor.chain().focus().toggleHeading({ level: 2 }).run()
              break
            case "h3":
              editor.chain().focus().toggleHeading({ level: 3 }).run()
              break
            case "paragraph":
              editor.chain().focus().setParagraph().run()
              break
            default:
              break
          }
        }}
      >
        <option value="paragraph">Normal</option>
        <option value="h1">Überschrift 1</option>
        <option value="h2">Überschrift 2</option>
        <option value="h3">Überschrift 3</option>
      </select>

      <div className="tw-flex">
        {/* List Controls */}
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={classNames(
            editor.isActive("bulletList") && "tw-bg-black tw-text-white",
            "tw-px-4 tw-py-2 tw-font-bold hover:tw-bg-gray-300"
          )}
        >
          <ListUnordered className="tw-size-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={classNames(
            editor.isActive("orderedList") && "tw-bg-black tw-text-white",
            "tw-px-4 tw-py-2 tw-font-bold hover:tw-bg-gray-300"
          )}
        >
          <ListOrdered className="tw-size-4" />
        </button>
      </div>

      <div className="tw-flex">
        {/* Formatting Controls */}
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={classNames(
            editor.isActive("bold") && "tw-bg-black tw-text-white",
            "tw-px-4 tw-py-2 tw-font-bold hover:tw-bg-gray-300"
          )}
        >
          <Bold className="tw-size-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={classNames(
            editor.isActive("italic") && "tw-bg-black tw-text-white",
            "tw-px-4 tw-py-2 tw-font-bold hover:tw-bg-gray-300"
          )}
        >
          <Italic className="tw-size-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={classNames(
            editor.isActive("strike") && "tw-bg-black tw-text-white",
            "tw-px-4 tw-py-2 tw-font-bold hover:tw-bg-gray-300"
          )}
        >
          <Strikethrough className="tw-size-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleHighlight().run()}
          className={classNames(
            editor.isActive("highlight") && "tw-bg-black tw-text-white",
            "tw-px-4 tw-py-2 tw-font-bold hover:tw-bg-gray-300"
          )}
        >
          <Highlighter className="tw-size-4" />
        </button>
      </div>

      <div className="tw-flex">
        {/* Alignment Controls */}
        <button
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
          className={classNames(
            editor.isActive({ textAlign: "left" }) && "tw-bg-black tw-text-white",
            "tw-px-4 tw-py-2 tw-font-bold hover:tw-bg-gray-300"
          )}
        >
          <AlignLeft className="tw-size-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
          className={classNames(
            editor.isActive({ textAlign: "center" }) && "tw-bg-black tw-text-white",
            "tw-px-4 tw-py-2 tw-font-bold hover:tw-bg-gray-300"
          )}
        >
          <AlignCenter className="tw-size-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
          className={classNames(
            editor.isActive({ textAlign: "right" }) && "tw-bg-black tw-text-white",
            "tw-px-4 tw-py-2 tw-font-bold hover:tw-bg-gray-300"
          )}
        >
          <AlignRight className="tw-size-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign("justify").run()}
          className={classNames(
            editor.isActive({ textAlign: "justify" }) && "tw-bg-black tw-text-white",
            "tw-px-4 tw-py-2 tw-font-bold hover:tw-bg-gray-300"
          )}
        >
          <AlignJustify className="tw-size-4" />
        </button>
      </div>

      <button
        onClick={() => setIsUploadImageModalOpen(true)}
        className={classNames("tw-px-4 tw-py-2 tw-font-bold hover:tw-bg-gray-300")}
      >
        <ImageIcon />
      </button>
    </div>
  )
}

function UploadImageModal({
  isOpen,
  setIsOpen,
  handleUpload,
}: {
  isOpen: boolean
  setIsOpen: (value: boolean) => void
  handleUpload: (file: File) => Promise<void>
}) {
  const [uploadedImage, setUploadedImage] = useState<File | undefined>()

  return (
    <Modal open={isOpen} onClose={() => setIsOpen(false)} title="Bild hochladen">
      <div className="tw-p-2">
        {uploadedImage ? (
          <div className="tw-flex tw-justify-center tw-rounded-lg tw-border tw-border-dashed tw-border-gray-900/25 tw-px-6 tw-py-10">
            <div className="tw-text-center">
              <img src={URL.createObjectURL(uploadedImage)} alt="Bild" />
              <div className="tw-mt-4 tw-flex tw-text-sm tw-leading-6 tw-text-gray-600">
                <label
                  htmlFor="file-upload"
                  className="tw-relative tw-cursor-pointer tw-rounded-md tw-bg-white tw-font-semibold tw-text-green-primary focus-within:tw-outline-none focus-within:tw-ring-2 focus-within:tw-ring-offset-green-primary focus-within:tw-ring-offset-2 hover:tw-text-green-secondary"
                >
                  <span>Bild hochladen</span>
                  <input
                    onChange={e => setUploadedImage(e.target.files?.[0])}
                    id="file-upload"
                    name="file-upload"
                    type="file"
                    className="tw-sr-only"
                    accept="image/png, image/jpeg"
                  />
                </label>
              </div>
            </div>
          </div>
        ) : (
          <div className="tw-flex tw-justify-center tw-rounded-lg tw-border tw-border-dashed tw-border-gray-900/25 tw-px-6 tw-py-10">
            <div className="tw-text-center">
              <Photo className="tw-mx-auto tw-h-12 tw-w-12 tw-text-gray-300" aria-hidden="true" />
              <div className="tw-mt-4 tw-flex tw-text-sm tw-leading-6 tw-text-gray-600">
                <label
                  htmlFor="file-upload"
                  className="tw-relative tw-cursor-pointer tw-rounded-md tw-bg-white tw-font-semibold tw-text-green-primary focus-within:tw-outline-none focus-within:tw-ring-2 focus-within:tw-ring-offset-green-primary focus-within:tw-ring-offset-2 hover:tw-text-green-secondary"
                >
                  <span>Bild hochladen</span>
                  <input
                    onChange={e => setUploadedImage(e.target.files?.[0])}
                    id="file-upload"
                    name="file-upload"
                    type="file"
                    className="tw-sr-only"
                    accept="image/png, image/jpeg"
                  />
                </label>
              </div>
            </div>
          </div>
        )}
        {uploadedImage && (
          <Button
            className="tw-mt-4"
            onClick={async () => {
              if (!uploadedImage) return
              const filesize = uploadedImage.size / 1024 / 1024 // get the filesize in MB
              if ((uploadedImage.type === "image/jpeg" || uploadedImage.type === "image/png") && filesize < 10) {
                await handleUpload(uploadedImage)
                setIsOpen(false)
                setUploadedImage(undefined)
              }
            }}
          >
            Einfügen
          </Button>
        )}
      </div>
    </Modal>
  )
}
